import type { CareersAnswerType } from "../../types/careerPage";

/** TanStack Query key prefixes for the public careers page. */
export const PUBLIC_CAREERS_QUERY_KEY = {
	PAGE: "public-careers-page",
	JOB: "public-careers-job",
} as const;

/** The root of every test id on the public careers page. */
export const TEST_ID = "public-careers";

/**
 * The hidden spam trap's field name, as the API reads it (`website`, which must arrive empty). A person never sees the
 * field; a bot that fills every input does. Its `autocomplete="off"` keeps browsers' and password managers' autofill
 * out of it, so a real applicant always sends it empty.
 */
export const HONEYPOT_FIELD = "website";

/** Field limits the API holds the form to (backend/models/careers.py and careers_page.py). */
export const APPLY_LIMITS = {
	NAME_MAX_LENGTH: 200,
	REFERRER_MAX_LENGTH: 120,
	ANSWER_MAX_LENGTH: 2000,
} as const;

/**
 * How a yes/no answer is stored. The same words whatever language the applicant read the page in, so the hiring team
 * reads one answer for one meaning.
 */
export const YES_NO_ANSWER = { yes: "Yes", no: "No" } as const;
export type YesNoChoice = keyof typeof YES_NO_ANSWER;

/** How the options picked on a `multi` question are joined into its one stored answer. */
export const MULTI_ANSWER_SEPARATOR = "; ";

/** The i18n key naming each question type's empty-answer problem. */
export const ANSWER_REQUIRED_KEY: Record<CareersAnswerType, string> = {
	yes_no: "publicCareers.apply.question.pick_one",
	single: "publicCareers.apply.question.pick_one",
	multi: "publicCareers.apply.question.pick_at_least_one",
	number: "publicCareers.apply.question.required",
	text: "publicCareers.apply.question.required",
	date: "publicCareers.apply.question.required",
};

/** The analytics events of the public page (anonymous Mixpanel only). */
export const PUBLIC_CAREERS_EVENT = {
	PAGE_VIEWED: "public_careers_page_viewed",
	APPLY_START: "public_careers_apply_start",
	APPLY_SUCCESS: "public_careers_apply_success",
	APPLY_ERROR: "public_careers_apply_error",
	CRASHED: "public_careers_page_crashed",
} as const;
